/**
 * Feature E03-F02-T04 — Pre-Kickoff analysis typed api-client + Query hook.
 *
 * Feature-owned (NOT in the shared queryKeys.ts / queries.ts). Uses the shared
 * `apiClient` (auto-injects the session Bearer token, normalizes the response,
 * never throws) + `unwrap` (the single throw-at-the-Query-boundary helper).
 * Contracts come from `@eer/shared` so the SPA speaks the exact same shapes as
 * the Fastify route (`POST /projects/:id/ai/pre_kickoff_analysis`, T03).
 *
 * 1:1 port of `../EER app/public/assets/js/project.js` handleAiAnalysis ~475:
 *   app.apiCall('ai_request', { projectId, action: 'pre_kickoff_analysis' })
 *
 * Legacy parity for errors: the route answers BOTH the success body
 * `{ success:true, analysis, drafts, projectId }` and the graceful-failure body
 * `{ success:false, error }` at HTTP 200 (the n8n Respond node always returned a
 * 200 the SPA branched on). So the union is parsed straight off the unwrapped
 * body — a `{ success:false }` is a value the caller branches on (toast the
 * `error`), NOT a thrown query error. A real transport/HTTP failure still throws
 * via `unwrap` and lands in `onError`.
 *
 * After a successful analysis the four artifacts (analysis + 3 drafts) are
 * already persisted by the service (T02); the caller refetches the project so
 * the DB stays the source of truth.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { z } from 'zod';
import {
  preKickoffAnalysisResponseSchema,
  preKickoffAnalysisEditResponseSchema,
  type PreKickoffAnalysisResponse,
  type PreKickoffAnalysisEdit,
  type PreKickoffAnalysisEditResponse,
  type PreKickoffDraftOption,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { qk } from '../api/queryKeys.js';

/**
 * The graceful-failure body (`{ success:false, error }`, HTTP 200) — the legacy
 * Respond parity branch. Mirrors the salary-search union pattern.
 */
export const preKickoffAnalysisFailureSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  projectId: z.number().int().optional(),
});
export type PreKickoffAnalysisFailure = z.infer<typeof preKickoffAnalysisFailureSchema>;

/** The route's HTTP-200 union: the success body OR the graceful-failure body. */
export const preKickoffAnalysisResultSchema = z.union([
  preKickoffAnalysisResponseSchema,
  preKickoffAnalysisFailureSchema,
]);
export type PreKickoffAnalysisResult = z.infer<typeof preKickoffAnalysisResultSchema>;

/** Per-project key so a refetch after analysis re-pulls the saved artifacts. */
export function preKickoffProjectQueryKey(projectId: number): readonly unknown[] {
  return ['prekickoff', 'project', projectId] as const;
}

/**
 * PK-QA-1 — the pre-kickoff analysis route answers a NON-overridable HTTP 400
 * `{ error: { code:'VALIDATION_ERROR', fields:string[] } }` when the project is
 * missing an invariant (client / role / contact email — the drafts are addressed
 * to the contact and an analysis with no client/role is meaningless). Unlike the
 * soft `CONFIRM_REQUIRED` 412 there is NO confirm path: the caller just surfaces
 * the missing fields. The raw body is recovered from a thrown error via
 * `errorBodyOf` (the client preserves it under `detail.details`).
 */
export const preKickoffValidationErrorSchema = z.object({
  error: z.object({
    code: z.literal('VALIDATION_ERROR'),
    fields: z.array(z.string()),
  }),
});

/**
 * Return the missing-field list when `body` is the pre-kickoff 400
 * `VALIDATION_ERROR` envelope, else `null` (so the caller can fall through to the
 * `CONFIRM_REQUIRED` / generic-error branches).
 */
export function readValidationFields(body: unknown): string[] | null {
  const parsed = preKickoffValidationErrorSchema.safeParse(body);
  return parsed.success ? parsed.data.error.fields : null;
}

/** Variables for the analysis mutation — the PK-QA-1 soft-gate override flag. */
export interface RunPreKickoffAnalysisVars {
  /**
   * Resend the SAME analysis request with `confirmed:true` to override the
   * `nothing_to_analyze` 412 soft gate (after the user confirms in the dialog).
   */
  readonly confirmed?: boolean;
}

/**
 * Run the pre-kickoff analysis sub-flow
 * (POST /projects/:id/ai/pre_kickoff_analysis). Returns the parsed HTTP-200
 * union; the SPA reads `res.success` to branch (render the analysis + drafts, or
 * toast `res.error`). Both branches are HTTP 200 (legacy parity), so the union is
 * parsed off the unwrapped body.
 *
 * PK-QA-1: a soft 412 `CONFIRM_REQUIRED(nothing_to_analyze)` and a hard 400
 * `VALIDATION_ERROR` are REAL HTTP errors (not the 200 `{success:false}` branch),
 * so they surface as a thrown `ApiResultError`; the caller inspects the preserved
 * body via `errorBodyOf`. `confirmed:true` overrides the soft gate on resend.
 */
export async function runPreKickoffAnalysis(args: {
  projectId: number;
  confirmed?: boolean;
}): Promise<PreKickoffAnalysisResult> {
  const result = await apiClient.post<unknown>(
    `/projects/${args.projectId}/ai/pre_kickoff_analysis`,
    args.confirmed ? { confirmed: true } : {},
    { command: 'ai_request' },
  );
  return preKickoffAnalysisResultSchema.parse(unwrap(result));
}

/**
 * Pre-kickoff analysis mutation (the "Run AI Analysis" button). Keyed by project;
 * on a SUCCESS body it invalidates the per-project key so the artifacts refetch
 * from the DB (source of truth — T02). A `{ success:false }` body resolves the
 * mutation (no throw); the caller toasts the error. A transport failure rejects
 * and lands in `onError`.
 *
 * NOTE: the api client strips `success:true` from object responses
 * (`unwrapLegacySuccess`), so `res.success` is `undefined` after the strip.
 * Success is determined by `isPreKickoffSuccess` which gates on the `analysis`
 * data field, not the stripped flag.
 *
 * E03-F02-T06 fix: this ALSO invalidates `qk.projects.detail(projectId)` — the
 * key `useProjectDetail`/`ProjectDetailView`/`ProjectDetailHeader` actually
 * read. `preKickoffProjectQueryKey` is invalidated too (kept for whatever else
 * may come to key off it later), but nothing subscribes a `useQuery` to it
 * today, so on its own the project detail card (and therefore the header's
 * analysis-status badge + this panel's "last analyzed" attribution once it
 * re-hydrates from a fresh page load) never refetched after a run.
 */
export function useRunPreKickoffAnalysis(
  projectId: number,
): UseMutationResult<PreKickoffAnalysisResult, ApiResultError, RunPreKickoffAnalysisVars> {
  const queryClient = useQueryClient();
  return useMutation<PreKickoffAnalysisResult, ApiResultError, RunPreKickoffAnalysisVars>({
    mutationFn: (vars) => runPreKickoffAnalysis({ projectId, confirmed: vars.confirmed === true }),
    onSuccess: (res) => {
      if (isPreKickoffSuccess(res)) {
        void queryClient.invalidateQueries({
          queryKey: preKickoffProjectQueryKey(projectId),
        });
        void queryClient.invalidateQueries({
          queryKey: qk.projects.detail(projectId),
        });
      }
    },
  });
}

/**
 * Narrow a result to the success body (type guard for the consumer).
 *
 * The api client strips `success:true` from object responses so `result.success`
 * is always `undefined` in the success branch. We discriminate on the `analysis`
 * field instead: it is required in `PreKickoffAnalysisResponse` and absent in
 * `PreKickoffAnalysisFailure`. The `{ success:false, error }` failure body is NOT
 * stripped (only truthy success flags are removed) and carries no `analysis`, so
 * it correctly falls through.
 */
export function isPreKickoffSuccess(
  result: PreKickoffAnalysisResult,
): result is PreKickoffAnalysisResponse {
  return 'analysis' in result && result.analysis !== undefined;
}

// ---------------------------------------------------------------------------
// PK4 (E03-F02-T08) — save inline edits of the persisted analysis brief
//   PATCH /projects/:id/analysis
// ---------------------------------------------------------------------------

/**
 * Persist inline edits of the Pre-Kickoff analysis brief
 * (PATCH /projects/:id/analysis, PK4). Unlike the AI run route this is honest
 * REST (200 / 404 / 400 — see the PK4 ADR, decision-log 2026-07-07): a NOT-FOUND
 * (no analysis artifact yet) or an INVALID patch is a real HTTP error, so it
 * flows through `unwrap` (throws `ApiResultError`) into the mutation's `onError`
 * — there is no `{ success:false }` graceful branch to disambiguate here. The
 * success body is the FULL merged analysis, validated against the shared schema
 * so a drifted shape fails loudly at the boundary.
 *
 * `patch` is a partial over the 10 editable brief fields; the caller builds it
 * with `diffAnalysisEdit` (only the changed fields) and must never send an empty
 * patch (the route 400s on it — the panel closes edit mode instead).
 */
export async function saveAnalysis(
  projectId: number,
  patch: PreKickoffAnalysisEdit,
): Promise<PreKickoffAnalysisEditResponse> {
  const result = await apiClient.patch<unknown>(`/projects/${projectId}/analysis`, patch, {
    command: 'update_analysis',
  });
  return preKickoffAnalysisEditResponseSchema.parse(unwrap(result));
}

/**
 * Save-analysis mutation (the PK4 "Save" button). On success it invalidates
 * `qk.projects.detail(projectId)` — the SAME key `useProjectDetail` reads — so
 * the panel re-hydrates the brief from the persisted artifact (source of truth),
 * and the read-only rows show the just-saved values. Errors surface via the
 * caller's `useToast` in `onError`; the failure body is a thrown `ApiResultError`
 * (404 no-analysis / 400 invalid patch / transport), read via `errorMessage`.
 */
export function useSaveAnalysis(
  projectId: number,
): UseMutationResult<PreKickoffAnalysisEditResponse, ApiResultError, PreKickoffAnalysisEdit> {
  const queryClient = useQueryClient();
  return useMutation<PreKickoffAnalysisEditResponse, ApiResultError, PreKickoffAnalysisEdit>({
    mutationFn: (patch) => saveAnalysis(projectId, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// B2 / KS-09 — Pre-Kickoff draft send (save → send)
// ---------------------------------------------------------------------------

/**
 * Save the edited draft, then send it.
 *
 * The two calls are ORDERED and the order is the whole point. The server's
 * `send_pre_kickoff_email` reads the STORED artifact `pre_kickoff_draft_<OPTION>`
 * and ignores the request body; that artifact holds whatever the AI analysis
 * wrote (services/preKickoffAnalysis.ts). Sending without saving first would
 * therefore mail the un-edited AI text and silently discard the recruiter's
 * edits — a data-loss bug wearing the costume of a working button. A failed save
 * ABORTS: better no email than the wrong email.
 *
 * KS-09 context: the Send button existed in `PreKickoffPanel` all along but was
 * gated on an `onSend` prop the only production render site never passed, and
 * these two endpoints had no caller anywhere in the SPA. Backend, api-client and
 * UI were each finished and none was connected to the next.
 */
export async function saveAndSendPreKickoffDraft(
  projectId: number,
  option: PreKickoffDraftOption,
  draft: { to: string; subject: string; body: string },
): Promise<void> {
  unwrap(
    await apiClient.post<unknown>(
      `/projects/${projectId}/prekickoff/draft`,
      { option, draft },
      { command: 'save_pre_kickoff_draft' },
    ),
  );
  unwrap(
    await apiClient.post<unknown>(
      `/projects/${projectId}/prekickoff/send`,
      { option },
      { command: 'send_pre_kickoff_email' },
    ),
  );
}

export interface SendPreKickoffDraftVars {
  readonly option: PreKickoffDraftOption;
  readonly draft: { to: string; subject: string; body: string };
}

/**
 * Send-draft mutation for the Stage-1 panel. Invalidates the project detail on
 * success — the send marks the artifact `sent` and writes a `project_event`, and
 * the panel re-hydrates its drafts from that same detail card.
 *
 * Errors are NOT swallowed: the route answers 404 (no draft), 503 (no sender
 * mailbox) and 502 (transport refused) with real messages, and the caller reads
 * them through `errorMessage` — the PK-QA-29 rule that a send failure must show
 * WHY, not a generic "Send failed".
 */
export function useSendPreKickoffDraft(
  projectId: number,
): UseMutationResult<void, ApiResultError, SendPreKickoffDraftVars> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, SendPreKickoffDraftVars>({
    mutationFn: ({ option, draft }) => saveAndSendPreKickoffDraft(projectId, option, draft),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
    },
  });
}
